/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  Button,
  Box,
  Slide,
  useTheme,
  Tooltip,
} from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import { debounce } from 'lodash';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { useSelector } from 'react-redux';
import DeleteDialog from '../../components/Common/DeleteDialog';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import KeyTabDialog from '../../components/KeyTabDialog';
import useOrganization from '../../hooks/UseOrganization';
import utils from '../../utils/Utils';
import {
  CommonTableCell,
  DateTimeCell,
} from '../../components/Common/CommonTableCell';
import SearchBox from '../../components/Common/SearchBox';

/**
 * @component APIKeySetting
 * @description APIKeySetting - Renders a component for managing ApiKeys details.
 * @returns {JSX.Element} APIKeySetting component.
 */
const APIKeySetting = () => {
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();

  const ExpireCell = ({ value }) => {
    const isDate = moment(value, moment.ISO_8601, true).isValid();

    if (!isDate) return <CommonTableCell value={value} />;

    return <DateTimeCell value={value} />;
  };
  ExpireCell.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const tableColumns = useMemo(
    () => [
      {
        heading: 'ID',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'id',
        Cell: CommonTableCell,
        width: 70,
      },
      {
        heading: 'Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'name',
        Cell: CommonTableCell,
        width: 100,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Product',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'product_name',
        Cell: CommonTableCell,
        width: 90,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Service',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'service_name',
        Cell: CommonTableCell,
        width: 90,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Description',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'description',
        Cell: CommonTableCell,
        width: 200,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Created On',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'created_at',
        Cell: DateTimeCell,
        width: 140,
      },
      {
        heading: 'Expire On',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'expires_at',
        Cell: ExpireCell,
        width: 140,
      },
    ],
    [utils, ExpireCell]
  );

  const [open, setOpen] = useState(false);
  const [editApiKey, setEditApiKey] = useState({});
  const { getApiKeyList, deleteApiKey } = useOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState('create');
  const [ApiKeyList, setApiKeyList] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAPIKey, setDeleteAPIKey] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQueryText, setSearchQueryText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setEditApiKey(props?.original);
          setMode('edit');
          setOpen(true);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          setShowDeleteModal(true);
          setDeleteAPIKey(props?.original);
        },
      },
    ],
    []
  );

  useEffect(() => {
    setIsLoading(true);
    getApiKeyList(orgName, pageIndex, pageSize, searchTerm).then((resp) => {
      setApiKeyList(resp?.data?.data);
      setPageCount(resp?.data?.max_pages);
      setTotalCount(resp?.data?.total_count);
      setIsLoading(false);
    });
  }, [orgName]);

  useEffect(() => {
    if (pageIndex >= 0 && pageSize > 0) {
      getApiKeyList(orgName, pageIndex, pageSize, searchTerm).then((resp) => {
        setApiKeyList(resp?.data?.data);
        setPageCount(resp?.data?.max_pages);
        setTotalCount(resp?.data?.total_count);
        // setPageIndex(resp?.data?.page);
      });
    }
  }, [pageIndex, pageSize, orgName]);

  useEffect(() => {
    getApiKeyList(orgName, 0, pageSize, searchTerm).then((resp) => {
      setApiKeyList(resp?.data?.data);
      setPageCount(resp?.data?.max_pages);
      setTotalCount(resp?.data?.total_count);
    });
  }, [searchTerm, orgName]);

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchApiKeyByName
   * @description searchApiKeyByName - Callback function to search ApiKeys by name.
   * @param {Event} e - The event object.
   */
  const searchApiKeyByName = (e) => {
    debouncedSave(e.target.value); // Call the debounced function
  };

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = useCallback(
    (searchText) => {
      setSearchQueryText(searchText);
    },
    [setSearchQueryText]
  );

  /**
   * @function handleDeleteApiKey
   * @description handleDeleteApiKey - Callback function to handle the deletion of a ApiKey.
   * @param {number} id - The ID of the ApiKey to be deleted.
   * @param {string} orgName - The ID of the organization to which the ApiKey belongs.
   */
  const handleDeleteApiKey = useCallback(
    (id, organizationIdData) => {
      deleteApiKey(id, organizationIdData).then(() => {
        getApiKeyList(organizationIdData, pageIndex, pageSize, searchTerm).then(
          (resp) => {
            setApiKeyList(resp?.data?.data);
            setPageCount(resp?.data?.max_pages);
            setTotalCount(resp?.data?.total_count);
            // setPageIndex(Number(resp?.data?.page) - 1);
          }
        );
      });
      setShowDeleteModal(false);
    },
    [deleteApiKey, getApiKeyList, pageIndex, pageSize, searchTerm]
  );

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setPageIndex(pageIndexData?.pageIndex);
      setPageSize(pageIndexData?.pageSize);
    },
    [setPageIndex, setPageSize]
  );

  return (
    <Grid item xs={12}>
      <Box margin="20px" display="flex" flexDirection="column" overflow="auto">
        <Typography variant="h3">API Keys</Typography>
        <Grid
          marginTop="32px"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="end"
          mt={2}
        >
          <Box width="200px">
            <SearchBox
              placeholder="Search"
              variant="filled"
              value={searchQueryText}
              onChange={searchApiKeyByName}
              onInput={(e) => handleSetSearchText(e.target.value)}
              aria-label="Search sub-admin"
            />
          </Box>
          <Button
            title="Create API Keys"
            variant="contained"
            onClick={() => {
              setOpen(true);
            }}
          >
            + Create API Keys
          </Button>
        </Grid>
        <Grid>
          {isLoading ? (
            <Box
              flexGrow={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              height="20vh"
            >
              <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Slide in={true} direction="up" mountOnEnter timeout={500}>
              {ApiKeyList?.length > 0 ? (
                <Box pt={2}>
                  <ReactMuiTableListView
                    data={ApiKeyList}
                    columns={tableColumns}
                    onTableStateChange={onTableStateChangeMethod}
                    getHeaderProps={() => ({
                      style: {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    })}
                    getRowProps={() => ({
                      style: {
                        position: 'relative',
                      },
                    })}
                    enableRowSelection={true}
                    pageCount={Number(pageCount)}
                    dataLength={Number(totalCount)}
                    enablePagination={true}
                    initialPageSize={5}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    rowLevelRightClickMenus={menus}
                    enableManualFilter={true}
                    enableControlledPagination={true}
                    showPaginationAction={false}
                    skipPageReset={true}
                    searchTerm={searchTerm}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  overflow="auto"
                  minHeight="300px"
                  height="calc(100vh - 438px)"
                >
                  {searchTerm ? (
                    <NoDataImage
                      style={{ color: theme.palette.primary.main }}
                    />
                  ) : (
                    <NoDataCreated
                      style={{ color: theme.palette.primary.main }}
                    />
                  )}
                  <Typography
                    variant="subtitle1"
                    textAlign="center"
                    sx={{ pt: '10px' }}
                  >
                    {searchTerm
                      ? 'No matching result found.'
                      : 'No keys found. Please create a key to proceed.'}
                  </Typography>
                </Box>
              )}
            </Slide>
          )}
        </Grid>
      </Box>
      <DeleteDialog
        open={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={() => handleDeleteApiKey(deleteAPIKey?.id, orgName)}
        confirmationMsg={
          <Typography variant="subtitle1">
            Are you sure you want to delete &quot;
            <Tooltip title={deleteAPIKey?.name || ''}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 110,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {deleteAPIKey?.name}
              </span>
            </Tooltip>
            &quot; key?
          </Typography>
        }
        resourceName="API Key"
      />
      <KeyTabDialog
        open={open}
        setOpen={setOpen}
        editApiKey={editApiKey}
        setMode={setMode}
        mode={mode}
        setApiKeyList={setApiKeyList}
        pageIndex={pageIndex}
        pageSize={pageSize}
        searchTerm={searchTerm}
        setPageCount={setPageCount}
        setTotalCount={setTotalCount}
      />
    </Grid>
  );
};

export default withTransaction('APIKeySetting', 'component')(APIKeySetting);
